import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { ProjectField } from '@/components/ProjectSelect';
import DocAccountsBlock from '@/shared/components/DocAccountsBlock';
import { refs } from '@/api/refs';
import { esvTipDoc, trimNum } from '@/shared/taxTips';
import {
  sickLeave,
  sickLeaveCalculation,
  type SickLeaveType,
  type SickLeaveCalculation,
} from '@/api/sick_leave';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import DatePopover from '@/components/DatePopover';
import DocumentToolbar from '@/shared/components/DocumentToolbar';
import { useConfirm } from '@/components/ConfirmProvider';
import InfoQ from '@/shared/components/InfoQ';
import { pickDefaultOrg } from '@/store/defaults';
import AttachmentsPanel from '@/shared/components/AttachmentsPanel';
import DocLayout from '@/shared/components/DocLayout';
import DocBlock, { type DocBlockState } from '@/shared/components/DocBlock';
import DocRail from '@/shared/components/DocRail';
import { useFormDraft } from '@/shared/hooks/useFormDraft';
import TchTable from '@/shared/components/TchTable';

const now = new Date();
const fmt2 = (v: string | number | null | undefined) =>
  v == null ? '—' : Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const TYPE_LABELS: Record<SickLeaveType, string> = {
  GENERAL_ILLNESS: 'Загальне захворювання',
  MATERNITY: 'Вагітність і пологи',
  CHILD_CARE: 'Догляд за дитиною',
  WORK_ACCIDENT: 'Виробничий нещасний випадок',
};
// WORK_ACCIDENT (нещасний випадок) тимчасово прибрано з UI (TECHDEBT — потребує
// окремої кореспонденції 656/ФССНВ + перевірки). Розрахунок backend підтримує.
// CHILD_CARE (догляд за дитиною) прибрано з МЕНЮ (рішення власника 2026-06-29) —
// як WORK_ACCIDENT. Розрахунок backend підтримує; TYPE_LABELS лишається для показу
// існуючих/імпортованих документів цього виду.
const TYPE_ORDER: SickLeaveType[] = ['GENERAL_ILLNESS', 'MATERNITY'];


interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  employee_id: string;
  sick_leave_type: SickLeaveType;
  start_date: string;
  end_date: string;
  certificate_number: string;
  certificate_date: string;
  diagnosis_code: string;
  insurance_stage_years: string;
  // ч.1118: страховий стаж за останні 12 місяців менший за 6 місяців (з листка).
  recent_stage_under_6m: boolean;
  comment: string;
}

const todayISO = now.toISOString().slice(0, 10);
const EMPTY: FormState = {
  organization_id: '', document_number: '', document_date: now.toISOString().slice(0, 16),
  employee_id: '', sick_leave_type: 'GENERAL_ILLNESS',
  start_date: todayISO, end_date: todayISO,
  certificate_number: '', certificate_date: todayISO,
  diagnosis_code: '', insurance_stage_years: '0', recent_stage_under_6m: false, comment: '',
};

function daysBetween(a: string, b: string): number {
  if (!a || !b) return 0;
  const d = (new Date(b).getTime() - new Date(a).getTime()) / 86400000;
  return d >= 0 ? Math.floor(d) + 1 : 0;
}

export default function SickLeaveEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg('documents.sick_leave.create');

  const docQuery = useQuery({
    queryKey: ['sick-leave', id],
    queryFn: () => sickLeave.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY);

  // Чернетка незбережених правок (2026-08-24): живе у браузері 10 год,
  // у базу нічого не пишеться. Повернувся на документ — запропонує відновити.
  useFormDraft(form, setForm, { ready: isNew || !!docQuery.data });
  const [preview, setPreview] = useState<SickLeaveCalculation | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  // id збереженого DRAFT-розрахунку (як у наказі відпустки) — щоб «Розрахувати»
  // оновлював чернетку (PATCH), а не дублював.
  const [existingCalcId, setExistingCalcId] = useState<string | null>(null);
  const [posted, setPosted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stageEdited, setStageEdited] = useState(false);
  // Ручний override середньоденної (бухгалтер може перебити пораховану).
  const [avgOverride, setAvgOverride] = useState('');
  // Проект / напрям (упр. облік) — потрапляє у субконто-3 проводок при проведенні.
  const [projectId, setProjectId] = useState('');
  const [docTab, setDocTab] = useState<'calc' | 'accounts'>('calc');
  const [expCode, setExpCode] = useState('949');
  // Незбережені правки → деактивують «Розрахувати» (preview застарів).
  const [dirty, setDirty] = useState(false);
  const confirm = useConfirm();

  const empQ = useQuery({
    queryKey: ['employees', form.organization_id],
    queryFn: () => refs.employees(form.organization_id, { active_only: true }),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (isNew) {
      // Авто-вибір org: availableOrgs (право create) з fallback на повний список
      // (OWNER/повільний /auth/me) — інакше organization_id порожній → save падає.
      const firstOrg = availableOrgs[0]?.id ?? pickDefaultOrg(orgList.data?.items)?.id ?? '';
      setForm((f) => f.organization_id ? f : { ...f, organization_id: firstOrg });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id, document_number: d.document_number,
        document_date: d.document_date.slice(0, 16), employee_id: d.employee_id,
        sick_leave_type: d.sick_leave_type, start_date: d.start_date, end_date: d.end_date,
        certificate_number: d.certificate_number, certificate_date: d.certificate_date,
        diagnosis_code: d.diagnosis_code ?? '',
        insurance_stage_years: d.insurance_stage_years,
        recent_stage_under_6m: !!d.recent_stage_under_6m, comment: d.comment ?? '',
      });
      setSavedId(d.id);
      setPosted(d.posted);
      setStageEdited(true);
      setDirty(false);
      if (d.sick_leave_calculation_id) {
        setExistingCalcId(d.sick_leave_calculation_id);
        sickLeaveCalculation.get(d.sick_leave_calculation_id).then((c) => {
          setPreview(c);
          setProjectId(c.project_id ?? '');
          setExpCode(c.expense_account_code ?? '949');
        }).catch(() => {});
      }
    }
  }, [isNew, docQuery.data, availableOrgs, orgList.data]);

  // Авто-стаж із hire_date обраного співробітника (підказка, редаговане).
  const selectedEmp = empQ.data?.find((e) => e.id === form.employee_id);
  useEffect(() => {
    if (stageEdited || !selectedEmp?.hire_date || !form.start_date) return;
    const yrs = (new Date(form.start_date).getTime() - new Date(selectedEmp.hire_date).getTime())
      / (365.25 * 86400000);
    if (yrs >= 0) {
      setForm((f) => ({ ...f, insurance_stage_years: yrs.toFixed(1) }));
    }
  }, [selectedEmp, form.start_date, stageEdited]);

  const days = daysBetween(form.start_date, form.end_date);
  const isPosted = posted || (preview?.posted ?? false);
  const isMaternity = form.sick_leave_type === 'MATERNITY';
  // ч.1118 (Порядок №1266, п.29): загальний стаж менше пів року означає, що й за
  // останні 12 місяців він менший за 6 — ознака вмикається сама. Для вагітності й
  // пологів загальний стаж не вводиться, там діє лише сама ознака.
  const stageForcesLow = !isMaternity && Number(form.insurance_stage_years || 0) < 0.5;
  const lowRecentStage = form.recent_stage_under_6m || stageForcesLow;
  // Працює в організації менше шести місяців до початку непрацездатності —
  // ознаку варто звірити з листком (стаж у попередніх роботодавців система не знає).
  const hiredRecently = !!selectedEmp?.hire_date && !!form.start_date
    && (new Date(form.start_date).getTime() - new Date(selectedEmp.hire_date).getTime())
      < 183 * 86400000;

  function buildCreate() {
    return {
      organization_id: form.organization_id,
      document_number: form.document_number || undefined,
      document_date: form.document_date,
      employee_id: form.employee_id,
      sick_leave_type: form.sick_leave_type,
      start_date: form.start_date, end_date: form.end_date,
      certificate_number: form.certificate_number,
      certificate_date: form.certificate_date,
      diagnosis_code: form.diagnosis_code || null,
      insurance_stage_years: form.insurance_stage_years || '0',
      recent_stage_under_6m: lowRecentStage,
      comment: form.comment || null,
    };
  }

  // Зберегти SickLeave (якщо ще нема) → повернути id.
  async function ensureSaved(): Promise<string> {
    if (savedId) return savedId;
    if (!form.organization_id) throw new Error('Оберіть організацію');
    if (!form.employee_id) throw new Error('Оберіть працівника');
    const doc = await sickLeave.create(buildCreate());
    setSavedId(doc.id);
    qc.invalidateQueries({ queryKey: ['sick-leaves'] });
    return doc.id;
  }

  // Зберегти лист (новий) → відкрити сторінку збереженого (як наказ відпустки).
  const create = useMutation({
    mutationFn: () => sickLeave.create(buildCreate()),
    onSuccess: (doc) => {
      setSavedId(doc.id); setDirty(false); setError(null);
      qc.invalidateQueries({ queryKey: ['sick-leaves'] });
      navigate(`/hr/sick-leaves/${doc.id}`);
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка збереження'),
  });

  // Розрахувати — ЗБЕРIГАЄ DRAFT-розрахунок у БД (як у наказі відпустки), а не лише
  // preview у пам'яті. Якщо розрахунок цього листа ВЖЕ є — ОНОВИТИ (PATCH), інакше
  // створити чернетку (createDraft). Так при переоткритті листа таблиця лишається.
  const calc = useMutation({
    mutationFn: () => {
      if (existingCalcId) {
        return sickLeaveCalculation.update(existingCalcId, {
          average_daily_override: avgOverride ? avgOverride : null,
          project_id: projectId || null,
          expense_account_code: expCode || null,
        });
      }
      return sickLeaveCalculation.createDraft({
        organization_id: form.organization_id, sick_leave_id: savedId!,
        document_date: form.document_date,
        average_daily_override: avgOverride ? avgOverride : null,
        project_id: projectId || null,
        expense_account_code: expCode || undefined,
      });
    },
    onSuccess: (r) => {
      setPreview(r); setExistingCalcId(r.id); setError(null);
      if (!avgOverride) setAvgOverride(trimNum(r.average_daily_wage));
      qc.invalidateQueries({ queryKey: ['sick-leaves'] });
      qc.invalidateQueries({ queryKey: ['sick-leave', savedId] });
      qc.invalidateQueries({ queryKey: ['sick-leave-calculations'] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка розрахунку'),
  });

  const post = useMutation({
    mutationFn: async () => {
      const sid = await ensureSaved();
      return sickLeaveCalculation.calculateAndPost({
        organization_id: form.organization_id, sick_leave_id: sid,
        document_date: form.document_date,
        average_daily_override: avgOverride ? avgOverride : null,
        project_id: projectId || null,
        expense_account_code: expCode || undefined,
      });
    },
    onSuccess: (r) => {
      // calculate-and-post (idempotency) міг видалити старий DRAFT і створити новий
      // проведений → оновити existingCalcId на актуальний id.
      setPreview(r); setExistingCalcId(r.id); setPosted(true);
      qc.invalidateQueries({ queryKey: ['sick-leaves'] });
      qc.invalidateQueries({ queryKey: ['sick-leave-calculations'] });
      if (savedId) navigate(`/hr/sick-leaves/${savedId}`);
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка проведення'),
  });

  // PATCH лікарняного (SickLeaveUpdate, без org/працівника). Включає тип+дати+дні —
  // інакше зміна виду/дат не зберігалась і розрахунок лишався по старому виду.
  function buildUpdate() {
    return {
      document_date: form.document_date,
      sick_leave_type: form.sick_leave_type,
      start_date: form.start_date,
      end_date: form.end_date,
      days_count: days,
      certificate_number: form.certificate_number,
      certificate_date: form.certificate_date,
      diagnosis_code: form.diagnosis_code || null,
      insurance_stage_years: form.insurance_stage_years || '0',
      recent_stage_under_6m: lowRecentStage,
      comment: form.comment || null,
    };
  }
  // Зберегти зміни вже збереженого лікарняного (PATCH) → дозволяє «Розрахувати» знову.
  const updateSick = useMutation({
    mutationFn: () => sickLeave.update(savedId!, buildUpdate() as never),
    onSuccess: () => {
      setError(null); setPreview(null); setDirty(false);
      qc.invalidateQueries({ queryKey: ['sick-leaves'] });
      qc.invalidateQueries({ queryKey: ['sick-leave', savedId] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка збереження'),
  });
  // Розпровести: «Провести» проводить РОЗРАХУНОК (sick_leave_calculation), сам лист
  // лишається DRAFT. Тож розпроводимо ЛИШЕ розрахунок (не лист — інакше 409 «Не проведено»).
  const unpostSick = useMutation({
    mutationFn: () => {
      if (!preview?.id) throw new Error('Немає проведеного розрахунку');
      return sickLeaveCalculation.unpost(preview.id);
    },
    onSuccess: (r) => {
      setPosted(false); setPreview(r); setError(null);
      qc.invalidateQueries({ queryKey: ['sick-leaves'] });
      qc.invalidateQueries({ queryKey: ['sick-leave-calculations'] });
      qc.invalidateQueries({ queryKey: ['sick-leave', savedId] });
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка розпроведення'),
  });
  // Видалити лікарняний.
  const removeSick = useMutation({
    mutationFn: () => sickLeave.remove(savedId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sick-leaves'] });
      qc.invalidateQueries({ queryKey: ['accruals-registry'] });
      navigate('/payrolls');
    },
    onError: (e: any) => setError(e?.response?.data?.detail ?? 'Помилка видалення'),
  });

  // Зберегти лист можна коли заповнені обов'язкові поля.
  const canSave = !!form.organization_id && !!form.employee_id && days > 0
    && !!form.certificate_number && !isPosted;
  // Розрахувати — лише для ЗБЕРЕЖЕНОГО листа без незбережених правок (як наказ відпустки).
  const canCalc = !!savedId && !dirty && !isPosted;

  // Зміна ключових полів → скинути розрахунок + dirty (треба зберегти і перерахувати).
  function setF(patch: Partial<FormState>) {
    setForm((f) => ({ ...f, ...patch }));
    if (!isPosted) { setPreview(null); setDirty(true); }
  }

  // ч.880 (запит власника): лікарняний має ДВІ виплати — за рахунок
  // роботодавця (перші 5 днів) і Фонду (ПФУ). «Повʼязані дії» → «створити
  // виплату» для кожної частини (kind SICK_ORG / SICK_PFU; підбір
  // неоплачених шукає sick_leave_calculations за місяцем початку лікарняного,
  // doc = id розрахунку). Частина без днів — disabled.
  const _sd = new Date(form.start_date || form.document_date);
  // ч.1347: виплата — зі збереженого розрахунку («розрахувати»), проведення не чекаємо.
  const _payTitle = (days: number | string | undefined, what: string) =>
    !existingCalcId ? 'Спершу розрахуйте лікарняний («розрахувати»)'
      : Number(days ?? 0) <= 0 ? `У цьому лікарняному немає днів ${what}`
      : `Виплата лікарняних ${what} на підставі цього розрахунку`;
  const _payTo = (kind: 'SICK_ORG' | 'SICK_PFU') => navigate(
    `/payroll-payments/new?org=${form.organization_id}&kind=${kind}`
    + `&year=${_sd.getFullYear()}&month=${_sd.getMonth() + 1}&doc=${existingCalcId}`);
  const railActions = [{
    label: 'створити виплату (роботодавець)',
    title: _payTitle(preview?.employer_days_count, 'за рахунок роботодавця'),
    disabled: !existingCalcId || Number(preview?.employer_days_count ?? 0) <= 0,
    onClick: () => _payTo('SICK_ORG'),
  }, {
    label: 'створити виплату (Фонд)',
    title: _payTitle(preview?.pfu_days_count, 'за рахунок Фонду'),
    disabled: !existingCalcId || Number(preview?.pfu_days_count ?? 0) <= 0,
    onClick: () => _payTo('SICK_PFU'),
  }, {
    // Третій крок циклу (KB payroll-vacation-sick): Фонд перерахував свою
    // частину на спецрахунок → «Надходження від Фонду» за період (Дт 313 — Кт 378).
    label: 'створити надходження від Фонду',
    title: !existingCalcId ? 'Спершу розрахуйте лікарняний («розрахувати»)'
      : Number(preview?.pfu_days_count ?? 0) <= 0 ? 'У цьому лікарняному немає частини Фонду'
      : 'Відшкодування Фондом за період лікарняного (закриває борг Фонду, Дт 313 — Кт 378)',
    disabled: !existingCalcId || Number(preview?.pfu_days_count ?? 0) <= 0,
    onClick: () => navigate(`/fund-receipts/new?org=${form.organization_id}`
      + `&year=${_sd.getFullYear()}&month=${_sd.getMonth() + 1}`),
  }];
  const blocks: DocBlockState[] = [
    { id: 'main', title: 'Основні дані', required: 1, filled: form.organization_id ? 1 : 0 },
    { id: 'emp', title: 'Працівник і лист непрацездатності', required: 3,
      filled: (form.employee_id ? 1 : 0) + (form.sick_leave_type ? 1 : 0) + (form.certificate_number ? 1 : 0) },
    { id: 'calc', title: 'Період і розрахунок', required: 2,
      filled: (form.start_date ? 1 : 0) + (form.end_date ? 1 : 0) },
    { id: 'extra', title: 'Коментарі та вкладення', required: 0, filled: 0 },
  ];
  const calcTitle = !savedId
    ? 'Спершу збережіть лікарняний — тоді можна розрахувати'
    : dirty
      ? 'Ви змінили дані. Спершу збережіть («Зберегти») — тоді розрахунок оновиться'
      : 'Перерахунок брутто/ПДФО/ВЗ/ЄСВ за дні × середню.';

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew && !savedId ? 'Нове нарахування (лікарняний)' : `Нарахування (лікарняний) № ${form.document_number}`}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center',
          flexWrap: 'wrap', marginLeft: 'auto' }}>
        <DocumentToolbar
          onBackToList={() => navigate('/payrolls')}
          onSave={() => isNew ? create.mutate() : updateSick.mutate()}
          saveDisabled={!canSave || create.isPending || updateSick.isPending}
          isPosted={isPosted}
          isNew={isNew && !savedId}
          postable={!!savedId}
          onPost={() => post.mutate()}
          postPending={post.isPending}
          postTitle="Розраховує та проводить лікарняний"
          onUnpost={() => unpostSick.mutate()}
          unpostPending={unpostSick.isPending}
          onDelete={() => { confirm('Видалити лікарняний?').then((ok) => { if (ok) removeSick.mutate(); }); }}
          deletePending={removeSick.isPending}
          entriesDoc={{ type: 'doc.sick_leaves', id: isNew ? '' : id! }}
          railMode
          extraActions={railActions}
        />
        </div>
      </div>

      {isPosted && (
        <div style={{ background: 'var(--badge-green-bg)', color: 'var(--badge-green-fg)', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>✓ Лікарняний проведено.</div>
      )}

      {error && (
        <div style={{ background: 'var(--badge-red-bg)', color: '#b00', padding: '0.5rem 0.75rem',
          borderRadius: 6, marginBottom: '0.75rem' }}>{error}</div>
      )}

      <DocLayout rail={
        <DocRail blocks={blocks} actions={railActions} isNew={isNew && !savedId}
          doc={{ type: 'doc.sick_leaves', id: isNew ? '' : id! }} />
      }>
      {/* ч.723 (розкладка власника): ряд 1 — Організація, Дата документа, Напрям;
          ряд 2 — Працівник, Причина, Діагноз, № листа, Дата листа;
          ряд 3 — Дата початку, Дата закінчення, Днів, Стаж, Середньоденна.
          ч.872: ряди стали блоками 1–3; обовʼязкові — « *» у підписі. */}
      <DocBlock index={1} title="Основні дані">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 5' }}>
          <label>Організація *</label>
          <select value={form.organization_id} disabled={!isNew || !!savedId}
            onChange={(e) => setF({ organization_id: e.target.value, employee_id: '' })}>
            <option value="">—</option>
            {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата документа</label>
          <DatePopover value={form.document_date.slice(0, 10)} disabled={isPosted}
            onChange={(v) => setF({ document_date: `${v}T00:00` })} />
        </div>
        <ProjectField span={4}
            value={projectId}
            organizationId={form.organization_id}
            onChange={(v) => setProjectId(v ?? '')}
            disabled={isPosted} />
      </div>
      </DocBlock>

      <DocBlock index={2} title="Працівник і лист непрацездатності">
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Працівник *</label>
          <select value={form.employee_id} disabled={isPosted}
            onChange={(e) => { setStageEdited(false); setF({ employee_id: e.target.value }); }}>
            <option value="">—</option>
            {empQ.data?.map((e) => <option key={e.id} value={e.id}>{e.name}{e.code ? ` — ${e.code}` : ''}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Причина (тип) *</label>
          <select value={form.sick_leave_type} disabled={isPosted}
            onChange={(e) => setF({ sick_leave_type: e.target.value as SickLeaveType })}>
            {TYPE_ORDER.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
          </select>
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Діагноз</label>
          <input value={form.diagnosis_code} disabled={isPosted} placeholder="код"
            onChange={(e) => setF({ diagnosis_code: e.target.value })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>№ листа *</label>
          <input value={form.certificate_number} disabled={isPosted}
            onChange={(e) => setF({ certificate_number: e.target.value })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Дата листа</label>
          <DatePopover value={form.certificate_date} disabled={isPosted} onChange={(v) => setF({ certificate_date: v })} />
        </div>
      </div>
      </DocBlock>

      <DocBlock index={3} title="Період і розрахунок"
        actions={!isPosted ? (
          <button className="primary" disabled={!canCalc || calc.isPending}
            onClick={() => calc.mutate()} title={calcTitle}>
            розрахувати
          </button>
        ) : undefined}>
      <div className="doc-grid">
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата початку *</label>
          <DatePopover value={form.start_date} disabled={isPosted} onChange={(v) => setF({ start_date: v })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 3' }}>
          <label>Дата закінчення *</label>
          <DatePopover value={form.end_date} disabled={isPosted} onChange={(v) => setF({ end_date: v })} />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Днів</label>
          <input value={days} readOnly disabled style={{ textAlign: 'center', background: 'var(--bg-soft)' }} />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Стаж (років)</label>
          <input type="number" step="0.1" min={0} value={form.insurance_stage_years}
            disabled={isPosted || isMaternity}
            title={isMaternity
              ? 'Для декретних завжди 100% — стаж не впливає'
              : selectedEmp?.hire_date
                ? `Стаж підставлено з дати прийому (${selectedEmp.hire_date}) — без попередніх місць роботи; перевірте за трудовою / реєстром ЦСПС`
                : undefined}
            onChange={(e) => { setStageEdited(true); setF({ insurance_stage_years: e.target.value }); }} />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Середньоденна</label>
          <input type="number" step="0.01" min={0} value={avgOverride} disabled={isPosted}
            placeholder="авто з заробітку"
            onChange={(e) => { setAvgOverride(e.target.value); if (!isPosted) setPreview(null); }} />
        </div>
      </div>
      {/* ч.1118: ознака з листка непрацездатності — Порядок №1266, п.29. */}
      <label style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', margin: '0 0 0.5rem',
        fontSize: '0.85rem' }}
        title={'Дані реєстру застрахованих осіб — видно в листку непрацездатності в кабінеті '
          + 'страхувальника. Тоді допомогу рахують із заробітку, але не вище мінімальної зарплати '
          + '(вагітність і пологи — не вище двох мінімальних і не нижче однієї).'}>
        <input type="checkbox" checked={lowRecentStage} disabled={isPosted || stageForcesLow}
          onChange={(e) => setF({ recent_stage_under_6m: e.target.checked })} />
        страховий стаж за останні 12 місяців менший за 6 місяців
        {stageForcesLow && (
          <span style={{ color: 'var(--text-secondary)' }}>
            (загальний стаж менше пів року)
          </span>
        )}
      </label>
      {!isPosted && hiredRecently && !lowRecentStage && (
        <p style={{ color: 'var(--badge-amber-fg)', fontSize: '0.8rem', margin: '0 0 0.5rem' }}>
          Працівник працює в організації менше шести місяців. Звірте з листком
          непрацездатності, чи має він шість місяців страхового стажу за останні дванадцять.
        </p>
      )}
      {/* ч.724: «розрахувати» — активна лише для збереженого листа без
          незбережених правок (як наказ відпустки); ч.872 — у заголовку блоку. */}
      {!isPosted && !savedId && (
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', margin: '0 0 0.5rem' }}>
          Кнопка «розрахувати» стане активною після збереження лікарняного.
        </p>
      )}
      {!isPosted && savedId && dirty && (
        <p style={{ color: 'var(--badge-amber-fg)', fontSize: '0.8rem', margin: '0 0 0.5rem' }}>
          Ви змінили дані. Натисніть «зберегти» — після цього можна перерахувати.
        </p>
      )}

      {/* Результат розрахунку */}
      {/* Закладки: розрахунок ↔ рахунки обліку (рішення власника 2026-07-07). */}
      {preview && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '0.5rem 0' }}>
          <div className="seg-tabs">
            <button type="button" className={docTab === 'calc' ? 'active' : ''}
              onClick={() => setDocTab('calc')}>Розрахунок</button>
          </div>
          <div className="seg-tabs" style={{ marginLeft: 'auto' }}>
            <button type="button" className={docTab === 'accounts' ? 'active' : ''}
              onClick={() => setDocTab('accounts')}>Рахунки обліку</button>
          </div>
        </div>
      )}
      {preview && docTab === 'accounts' && form.employee_id && (
        <DocAccountsBlock
          organizationId={form.organization_id}
          employeeIds={[form.employee_id]}
          paymentKind="SICK"
          headerCodes={{ expense: expCode, payroll: '663', pdfo: '6411', wz: '642', esv: '651' }}
          expenseCode={expCode}
          onExpenseChange={(v) => setExpCode(v)}
          disabled={isPosted}
          nameOf={() => selectedEmp?.name ?? '—'}
        />
      )}
      {docTab === 'calc' && preview && (
        <div style={{ marginTop: '0.5rem' }}>
          {/* Інфо над таблицею. */}
          <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.82rem',
            color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
            <span><strong>Працівник:</strong> {selectedEmp?.name ?? '—'}</span>
            <span><strong>Період:</strong> {preview.calc_period_start} — {preview.calc_period_end}</span>
            <span><strong>Середня:</strong> {fmt2(preview.average_daily_wage)}{preview.average_from_import ? ' ⚠' : ''}</span>
            <span><strong>% за стажем:</strong> {preview.applied_percentage_rate}%</span>
          </div>
          {/* ч.1118: з чого взято середню і які межі закону застосовано. */}
          {preview.calc_note && (
            <div style={{ margin: '0 0 0.5rem', padding: '0.4rem 0.6rem', borderRadius: 6,
              background: 'var(--bg-soft)', border: '1px solid var(--border)', fontSize: '0.8rem',
              whiteSpace: 'pre-line' }}>
              {preview.calc_note}
            </div>
          )}
          {/* Широка таблиця: 2 рядки (Роботодавець / Фонд) + Разом. */}
          <div style={{ overflowX: 'auto' }}>
          <TchTable>
            <thead>
              <tr style={{ textAlign: 'right' }}>
                <th style={{ textAlign: 'left' }}>Джерело</th>
                <th>Днів</th>
                <th>Брутто</th>
                <th>ПДФО</th>
                <th>ВЗ</th>
                <th>ЄСВ</th>
                <th>До виплати</th>
              </tr>
            </thead>
            <tbody>
              {/* Роботодавець — лише якщо є його дні (перші 5 при загальному захворюванні).
                  Догляд за дитиною / пологи — все за рахунок Фонду, рядка роботодавця немає. */}
              {Number(preview.employer_days_count) > 0 && (
                <tr style={{ textAlign: 'right' }}>
                  <td style={{ textAlign: 'left' }}>Роботодавець</td>
                  <td>{preview.employer_days_count}</td>
                  <td>{fmt2(preview.employer_amount)}</td>
                  <td>{fmt2(preview.employer_pdfo_amount ?? 0)}</td>
                  <td>{fmt2(preview.employer_wz_amount ?? 0)}</td>
                  <td>{fmt2(preview.employer_esv_amount ?? 0)}</td>
                  <td>{fmt2(preview.employer_net_amount ?? 0)}</td>
                </tr>
              )}
              {Number(preview.pfu_days_count) > 0 && (
                <tr style={{ textAlign: 'right', borderTop: '1px solid #eee' }}>
                  <td style={{ textAlign: 'left' }}>
                    {preview.funding_source_marker === 'WORK_ACCIDENT_FUND' ? 'Фонд НВ' : 'Фонд (ПФУ)'}
                  </td>
                  <td>{preview.pfu_days_count}</td>
                  <td>{fmt2(preview.pfu_amount)}</td>
                  <td>{fmt2(preview.pfu_pdfo_amount ?? 0)}</td>
                  <td>{fmt2(preview.pfu_wz_amount ?? 0)}</td>
                  <td>{fmt2(preview.pfu_esv_amount ?? 0)}</td>
                  <td>{fmt2(preview.pfu_net_amount ?? 0)}</td>
                </tr>
              )}
              <tr style={{ textAlign: 'right', fontWeight: 700 }}>
                <td style={{ textAlign: 'left' }}>Разом</td>
                <td>{preview.days_count}</td>
                <td>{fmt2(preview.total_gross_amount)}</td>
                <td>
                  {fmt2(preview.pdfo_amount)} <InfoQ title={isMaternity
                    ? 'Декретні не оподатковуються ПДФО (Закон 1105)'
                    : `Податок на доходи фізосіб — ${trimNum(preview.pdfo_rate)}% від брутто.`} />
                </td>
                <td>
                  {fmt2(preview.wz_amount)} <InfoQ title={isMaternity
                    ? 'Декретні не оподатковуються військовим збором (Закон 1105)'
                    : `Військовий збір — ${trimNum(preview.wz_rate)}% від брутто.`} />
                </td>
                <td>
                  {fmt2(preview.esv_amount)} <InfoQ title={esvTipDoc(preview.esv_amount, preview.esv_rate)} />
                </td>
                <td>{fmt2(preview.net_amount)}</td>
              </tr>
            </tbody>
          </TchTable>
          </div>

          {/* Платіжка ЄСВ з частини Фонду — для пакета документів у Фонд. */}
          {Number(preview.pfu_esv_amount ?? 0) > 0 && (
            <div style={{ marginTop: '0.5rem', padding: '0.4rem 0.6rem', borderRadius: 6,
              background: 'var(--primary-soft)', border: '1px solid var(--badge-blue-bd)', fontSize: '0.82rem' }}>
              💸 ЄСВ до сплати з частини Фонду: <strong>{fmt2(preview.pfu_esv_amount ?? 0)}</strong> грн
              (окрема платіжка для пакета документів у Фонд).
            </div>
          )}

          {preview.average_from_import && (
            <div style={{ marginTop: '0.5rem', padding: '0.4rem 0.6rem', borderRadius: 6,
              background: 'var(--badge-amber-bg)', border: '1px solid var(--badge-amber-bd)', fontSize: '0.8rem',
              color: 'var(--badge-amber-fg)' }}>
              ⚠ Середня врахувала імпортовані з 1С нарахування — перевірте суму.
            </div>
          )}

          {(preview.monthly_parts?.length ?? 0) > 1 && (
            <div style={{ marginTop: '0.75rem' }}>
              <h4 style={{ margin: '0 0 0.3rem', fontSize: '0.85rem', color: 'var(--badge-amber-fg)' }}>
                По місяцях (перехідний лікарняний — для звітності)
              </h4>
              <TchTable>
                <thead>
                  <tr style={{ textAlign: 'right' }}>
                    <th style={{ textAlign: 'left' }}>Місяць</th>
                    <th>Днів</th>
                    <th>Роботодавець</th>
                    <th>ПФУ/Фонд</th>
                    <th>Брутто</th>
                    <th>ПДФО</th>
                    <th>ВЗ</th>
                    <th>ЄСВ</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.monthly_parts!.map((m) => (
                    <tr key={`${m.period_year}-${m.period_month}`} style={{ textAlign: 'right', borderTop: '1px solid #eee' }}>
                      <td style={{ textAlign: 'left' }}>
                        {m.period_year}-{String(m.period_month).padStart(2, '0')}
                      </td>
                      <td>{m.days_in_month}</td>
                      <td>{m.employer_days_in_month} дн · {fmt2(m.employer_amount)}</td>
                      <td>{m.pfu_days_in_month} дн · {fmt2(m.pfu_amount)}</td>
                      <td>{fmt2(m.total_gross_amount)}</td>
                      <td>{fmt2(m.pdfo_amount)}</td>
                      <td>{fmt2(m.wz_amount)}</td>
                      <td>{fmt2(m.esv_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </TchTable>
            </div>
          )}
        </div>
      )}
      </DocBlock>

      <DocBlock index={4} title="Коментарі та вкладення">
      <div className="docblock__cols">
        {/* ч.872: поле коментаря — state/payload були, input у формі не було. */}
        <div className="field" style={{ margin: 0 }}>
          <label>Коментар</label>
          <input value={form.comment} disabled={isPosted}
                 onChange={(e) => setF({ comment: e.target.value })} />
        </div>
        <div>
          {/* Вкладення (2026-08-13): лікарняний лист — скан обовʼязково. */}
          <AttachmentsPanel organizationId={form.organization_id}
            documentType={'doc.sick_leaves'} documentId={id} />
        </div>
      </div>
      </DocBlock>
      </DocLayout>
    </div>
  );
}

